import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Check, Stethoscope } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';

const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

const Services = () => {
  const { currentLanguage, t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  useSeo('services', currentLanguage);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/services/catalog`, {
          params: { lang: currentLanguage },
        });
        if (cancelled) return;
        setCategories(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error('Failed to load services catalog:', err);
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  return (
    <div className="pt-12 pb-16 container-custom px-4 space-y-10">
        <header className="glass-card p-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-3 text-[#0f172a]">
          {t('services.title', 'Services & Treatments')}
        </h1>
        <p className="text-[#475569] text-lg">
          {t(
            'services.subtitle',
            'A full directory of dental specialities at Crystal Dental — explore each category and the treatments inside.'
          )}
        </p>
      </header>

      {loading ? (
        <div className="glass-card p-8 text-center text-[#64748b]">Loading…</div>
      ) : categories.length === 0 ? (
        <div className="glass-card p-8 text-center text-[#64748b]">
          No services available yet.
        </div>
      ) : (
        <div className="space-y-12">
          {categories.map((cat) => (
            <section key={cat.id} className="glass-card p-0 overflow-hidden">
              <div className="grid md:grid-cols-[1.1fr,1fr] gap-0">
                <div className="p-8 md:p-10 flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-700 bg-sky-50 px-3 py-1 rounded-full mb-4">
                      <Sparkles size={14} />
                      {String(cat.sort_order || 0).padStart(2, '0')} · {t('services.category', 'Category')}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-3">
                      {cat.title || cat.slug}
                    </h2>
                    {cat.short_description ? (
                      <p className="text-[#475569] leading-7 mb-5">{cat.short_description}</p>
                    ) : null}
                    <Link
                      to={`/services/${cat.slug}`}
                      className="inline-flex items-center gap-2 text-sky-700 font-semibold hover:text-sky-800"
                    >
                      {t('common.learn_more', 'Learn more')}
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
                {cat.image_url ? (
                  <div className="aspect-[16/10] md:aspect-auto md:min-h-[260px] overflow-hidden">
                    <img
                      src={cat.image_url}
                      alt={cat.title || cat.slug}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-sky-100 to-sky-200 grid place-items-center min-h-[260px]">
                    <Sparkles className="text-sky-500" size={42} />
                  </div>
                )}
              </div>

              {Array.isArray(cat.treatments) && cat.treatments.length > 0 && (
                <div className="border-t border-slate-100 bg-white/60">
                  <div className="bg-gradient-to-r from-sky-700 via-sky-600 to-sky-500 text-white px-6 md:px-8 py-4 flex items-center justify-between">
                    <h3 className="inline-flex items-center gap-3 text-base md:text-lg font-bold uppercase tracking-[0.18em]">
                      <span className="w-9 h-9 rounded-full bg-white/20 ring-1 ring-white/40 grid place-items-center">
                        <Stethoscope size={18} />
                      </span>
                      {t('services.treatments', 'Treatments')}
                    </h3>
                    <span className="hidden sm:inline-flex items-center gap-1.5 bg-white/15 ring-1 ring-white/30 rounded-full px-3 py-1 text-xs font-semibold tracking-wider">
                      {cat.treatments.length}{' '}
                      {cat.treatments.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {cat.treatments.map((tr) => (
                        <Link
                          key={tr.id}
                          to={`/services/${tr.slug}`}
                          className="group flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
                        >
                          <span className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 grid place-items-center group-hover:bg-sky-600 group-hover:text-white transition-colors">
                            <Check size={14} />
                          </span>
                          <span className="flex-1">
                            <span className="block font-semibold text-[#0f172a] group-hover:text-sky-700">
                              {tr.title || tr.slug}
                            </span>
                            {tr.savings_percent ? (
                              <span className="text-xs text-emerald-600 font-semibold">
                                {tr.savings_percent}% {t('services.save_vs_uk', 'savings vs UK')}
                              </span>
                            ) : tr.short_description ? (
                              <span className="text-xs text-slate-500 line-clamp-1">{tr.short_description}</span>
                            ) : null}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default Services;
